"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
  TestimonialItem,
} from "@/components/feature-template/types";
import { HeartPulse, Newspaper, ShieldCheck } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/resources/layoffs-tracker";
const APP = "/sign-up";

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "layoffs-feed",
    name: "Layoffs Feed",
    title: "Every Tech Layoff, in One Feed",
    description:
      "Company, roles cut, teams affected and date, gathered from public announcements and updated as news breaks.",
    imageSrcUrl: `${IMG}/hero-layoffs-feed.png`,
    primaryActionText: "Track Layoffs",
    primaryActionLink: APP,
    secondaryActionText: "Browse Jobs",
    secondaryActionLink: "/jobs/board",
  },
  {
    id: "company-health",
    name: "Company Health",
    title: "Check a Company Before You Apply",
    description:
      "Recent layoffs, hiring freezes, open-role trends and funding news for any company, summed up as Healthy, Watch or At risk.",
    imageSrcUrl: `${IMG}/hero-company-health.png`,
    primaryActionText: "Check a Company",
    primaryActionLink: APP,
    secondaryActionText: "Jobs Deep Research",
    secondaryActionLink: "/features/jobs-deep-research",
  },
  {
    id: "freeze-alerts",
    name: "Freeze Alerts",
    title: "Know When a Company Stops Hiring",
    description:
      "Get an alert when a company on your watchlist announces layoffs or pauses hiring, and when it opens roles that match you.",
    imageSrcUrl: `${IMG}/hero-freeze-alerts.png`,
    primaryActionText: "Set Up Alerts",
    primaryActionLink: APP,
    secondaryActionText: "Hiring Trends",
    secondaryActionLink: "/resources/hiring-trends",
  },
];

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "layoffs-tracker",
    title: "Layoffs Tracker",
    subtitle: "UPDATED AS NEWS BREAKS",
    description:
      "Filter layoffs by region, industry, team and company size. Each entry shows the roles affected, the share of staff, the date and a link to the original source.",
    benefits: [
      "Filter by region and industry",
      "Teams and roles affected",
      "Source on every entry",
      "Last 90 days at a glance",
    ],
    primaryCTA: "Track Layoffs",
    primaryLink: APP,
    secondaryCTA: "Browse Jobs",
    secondaryLink: "/jobs/board",
    imageSrcUrl: `${IMG}/layoffs-table.png`,
    icon: <Newspaper className="w-4 h-4" />,
    stats: "Source linked on every entry",
  },
  {
    id: "company-health-signals",
    title: "Company Health Signals",
    subtitle: "HEALTHY, WATCH OR AT RISK",
    description:
      "Layoff history, hiring freezes, open-role trends and funding news combined into one status for each company, with every signal shown so you can see why.",
    benefits: [
      "One clear status per company",
      "Layoff and freeze history",
      "Open-role trend",
      "Funding news",
    ],
    primaryCTA: "Check Company Health",
    primaryLink: APP,
    secondaryCTA: "Jobs Deep Research",
    secondaryLink: "/features/jobs-deep-research",
    imageSrcUrl: `${IMG}/health-signals.png`,
    icon: <HeartPulse className="w-4 h-4" />,
    stats: "Every signal visible",
    orientation: "reversed",
  },
];

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "hiring-health-filter",
    title: "Hiring-Health Filter",
    subtitle: "BUILT INTO THE JOB BOARD",
    description:
      "Hide jobs from companies with recent layoffs or a hiring freeze, so your applications go to employers that are reviewing them. Prefer to see everything? Switch it off and get a warning badge instead.",
    benefits: [
      "Hides at-risk employers",
      "Flags hiring freezes",
      "Warning badges if filter is off",
      "Turn on or off any time",
    ],
    primaryCTA: "Browse Healthy Jobs",
    primaryLink: "/jobs/board",
    secondaryCTA: "Hiring Trends",
    secondaryLink: "/resources/hiring-trends",
    imageSrcUrl: `${IMG}/hiring-health-filter.png`,
    icon: <ShieldCheck className="w-4 h-4" />,
    stats: "On the job board",
  },
];

// Replace with real user quotes before publishing.
const testimonials: TestimonialItem[] = [
  {
    id: 1,
    text: "Two companies on my list froze hiring the same month. The alert saved me weeks of waiting on applications that were never going to move.",
    name: "Priya M.",
    role: "Marketing Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 2,
    text: "I check the health status before every application now. It takes ten seconds and I only apply where teams are growing.",
    name: "Marcus T.",
    role: "DevOps Engineer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 3,
    text: "After my own layoff I wanted somewhere stable. The tracker showed me which companies were still hiring in my field.",
    name: "Alexander",
    role: "Content Writer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=600&h=800&fit=crop&q=80",
  },
];

export default function LayoffsTrackerPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "Check the Company",
            titleHighlight: "Before You Apply.",
            subtitle:
              "Follow layoffs and hiring freezes across tech, see each company's hiring health, and spend your applications on employers that are actually hiring.",
            ctaText: "Track Layoffs",
            ctaLink: APP,
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Apply Where Hiring Is Real.",
            sectionSubtitle:
              "Know which companies are cutting and which are still growing.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "testimonials",
          data: {
            title: "Fewer Dead Ends",
            subtitle: "From job seekers who checked first",
            testimonials,
          },
        },
        {
          type: "faq",
          data: {
            title: "Questions About the Layoffs Tracker",
            subtitle: "Sources, updates and how to read company health.",
            faqs,
            ctaTitle: "See who is hiring before you apply.",
            ctaText: "Track Layoffs",
            ctaLink: APP,
          },
        },
      ]}
    />
  );
}
